import AutoZipCodeForm from '@root/joinroot.com/src/components/forms/auto-zip-code-form';
import AvailabilityMap from '@root/joinroot.com/src/components/sections/availability-map/availability-map';
import BodyCopy from '@root/gatsby-contentful-core/src/components/body-copy';
import Colors from '@root/gatsby-contentful-core/src/utils/colors';
import Modal from '@root/gatsby-contentful-core/src/components/modal';
import PropTypes from '@root/vendor/prop-types';
import React, { useState } from '@root/vendor/react';
import Responsive from '@root/core/src/utils/responsive';
import RootUnavailableFormFlow from '@root/joinroot.com/src/components/forms/root-unavailable-form-flow';
import useAnalytics from '@root/core/src/hooks/use-analytics';
import useVisibilityAnalytics from '@root/core/src/hooks/use-visibility-analytics';
import { Eyebrow, H1Display, H2 } from '@root/gatsby-contentful-core/src/components/text';
import { FORM_FLOWS } from '@root/joinroot.com/src/components/forms/zip-code-form';
import { StyleSheet } from '@root/core/src/utils/styles';
import { constantize } from '@root/core/src/utils/strings';

export const TITLE_TYPES = {
  H1: 'H1',
  H2: 'H2',
};

export default function AvailabilityMapSection({
  anchor,
  body,
  eyebrow,
  form = null,
  sectionSpacing = {},
  titleType,
}) {
  const { nodeRef, trackClick } = useVisibilityAnalytics('AVAILABILITY_MAP');
  const analytics = useAnalytics('AVAILABILITY_MAP');
  const [selectedState, setSelectedState] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const HeadlineComponent = titleType === TITLE_TYPES.H1 ? H1Display : H2;

  const headingContainerClass = titleType.toLowerCase() === 'h1' ? 'heading1' : 'heading2';
  const sectionSpacingStyles = {
    paddingTop: sectionSpacing.top / 1.25,
    paddingBottom: sectionSpacing.bottom / 1.25,
    ...Responsive.md({
      paddingTop: sectionSpacing.top,
      paddingBottom: sectionSpacing.bottom,
    }),
  };

  function onClick(event, state) {
    if (isModalVisible) { return; }

    trackClick(constantize(state.name), {
      stateConfig: state,
    });

    if (state.page) {
      return;
    }

    event.preventDefault();

    setIsModalVisible(true);
    setSelectedState(state);
  }

  return (
    <section
      css={[styles.availabilityMapSection, sectionSpacingStyles]}
      id={anchor}
      ref={nodeRef}
    >
      <div
        className={headingContainerClass}
        css={styles.headingContainer}
      >
        {eyebrow && <Eyebrow css={styles.eyebrow}>{eyebrow}</Eyebrow>}

        <HeadlineComponent css={styles.title}>
          <span className={'title-root-orange'}>Where we are</span> and <span className={'title-root-orange-light'}>will be soon</span>
        </HeadlineComponent>

        <BodyCopy
          content={body}
          css={styles.body}
        />
      </div>
      <AvailabilityMap onClick={onClick} />
      {form &&
        <AutoZipCodeForm
          analytics={analytics}
          formBody={'We’re taking fair car insurance to the entire nation. Here are the states we already serve and the ones we’re launching soon. Live in a state not yet covered by Root? Get notified when our car insurance becomes available.'}
          formFlow={FORM_FLOWS.AUTO}
          formTitle={'Is Root available in your area?'}
          namePrefix={'availability-map'}
        />
      }
      {selectedState &&
        <Modal
          isShowing={isModalVisible}
          name={'availability-map-modal'}
          onClose={() => setIsModalVisible(false)}
        >
          <RootUnavailableFormFlow
            analytics={analytics}
            market={selectedState.name}
            onClose={() => setIsModalVisible(false)}
            product={constantize(FORM_FLOWS.AUTO)}
          />
        </Modal>
      }
    </section>
  );
}

const styles = StyleSheet.create({
  availabilityMapSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    textAlign: 'center',
    '@media screen and (min-width: 768px)': {
      padding: '100px 20px',
    },
  },
  headingContainer: {
    '&.heading1': {
      maxWidth: 335,
    },
    '&.heading2': {
      maxWidth: 250,
    },
    marginBottom: 50,
    ...Responsive.md({
      '&.heading1': {
        maxWidth: 940,
      },
      '&.heading2': {
        maxWidth: 700,
      },
    }),
  },
  eyebrow: {
    marginBottom: 15,
  },
  title: {
    margin: '0 auto 15px',
    '& .title-root-orange': {
      color: Colors.rootOrange(),
    },
    '& .title-root-orange-light': {
      color: Colors.rootOrangeLight(),
    },
    '@media screen and (min-width: 768px)': {
      maxWidth: 'none',
    },
  },
  body: {
    maxWidth: 460,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
});

AvailabilityMapSection.propTypes = {
  anchor: PropTypes.string,
  body: PropTypes.node,
  eyebrow: PropTypes.node,
  form: PropTypes.node,
  sectionSpacing: PropTypes.shape({
    bottom: PropTypes.number,
    top: PropTypes.number,
  }),
  titleType: PropTypes.oneOf(Object.values(TITLE_TYPES)),
};
